import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { useDateFormatter } from '../hooks/useDateFormatter';
import DateTimePickerInput from './DateTimePickerInput';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface DuplicateProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  problem: any;
  onSuccess?: (newProblemNumber: string) => void; // Optional callback when problem is duplicated
}

const DuplicateProblemModal: React.FC<DuplicateProblemModalProps> = ({
  isOpen,
  onClose,
  problem,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const { getToday } = useDateFormatter();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    contactId: '',
    impact: 'low_3',
    urgency: 'low_3',
    priority: 'planning_5',
    problemStatement: '',
    problemDate: '',
    customerId: '',
    engagementId: '',
    resourceId: '',
    resourceIdValue: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: ''
  });
  // Store resource object separately for reliable display
  const [selectedResource, setSelectedResource] = useState<{ id: string; name: string; resourceId: string } | null>(null);
  const [isOtherResource, setIsOtherResource] = useState(false);

  // Combobox query states
  const [contactQuery, setContactQuery] = useState('');
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');

  // Static options - values must match backend enum (CaseImpact, CaseUrgency)
  const impactOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const urgencyOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements'],
    queryFn: async () => {
      const response = await api.get('/engagements');
      return response.data;
    }
  });

  // Fetch assignment groups (only type=assignment)
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'assignment'],
    queryFn: async () => {
      const response = await api.get('/groups', { params: { type: 'assignment', limit: 1000 } });
      return response.data;
    }
  });

  // Fetch resources based on engagement
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return [];
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!formData.engagementId
  });

  // Fetch contacts for Contact field (filtered by engagement)
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) {
        return { contacts: [] };
      }
      const response = await api.get(`/contacts/dropdown?engagementId=${formData.engagementId}`);
      return response.data;
    },
    enabled: !!formData.engagementId,
    staleTime: 0,
    refetchOnMount: true
  });

  // Fetch primary contact for auto-selection
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch users based on assignment group
  const { data: usersData } = useQuery({
    queryKey: ['group-users', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Priority calculation based on Impact and Urgency
  const calculatePriority = (impact: string, urgency: string): string => {
    const normalizeValue = (value: string): string => {
      if (!value) return 'low';
      if (value.startsWith('low')) return 'low';
      if (value.startsWith('medium')) return 'medium';
      if (value.startsWith('high')) return 'high';
      return value;
    };

    const normalizedImpact = normalizeValue(impact);
    const normalizedUrgency = normalizeValue(urgency);

    const matrix: Record<string, Record<string, string>> = {
      'high': { 'high': 'critical_1', 'medium': 'high_2', 'low': 'moderate_3' },
      'medium': { 'high': 'high_2', 'medium': 'moderate_3', 'low': 'low_4' },
      'low': { 'high': 'moderate_3', 'medium': 'low_4', 'low': 'planning_5' }
    };

    return matrix[normalizedImpact]?.[normalizedUrgency] || 'moderate_3';
  };

  // Auto-calculate priority when impact or urgency changes
  useEffect(() => {
    if (formData.impact && formData.urgency) {
      const calculatedPriority = calculatePriority(formData.impact, formData.urgency);
      setFormData(prev => ({ ...prev, priority: calculatedPriority }));
    }
  }, [formData.impact, formData.urgency]);

  // Find and set resource when resourceId, resourceIdValue, or resources change
  useEffect(() => {
    if (resourcesData && !isOtherResource && (formData.resourceId || formData.resourceIdValue)) {
      const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];

      // Try to find by resourceId (UUID) first
      let resource = formData.resourceId
        ? resourcesArray.find((r: any) => r.id === formData.resourceId)
        : null;

      // If not found by ID, try by resourceIdValue (subscription string)
      if (!resource && formData.resourceIdValue) {
        resource = resourcesArray.find((r: any) => r.resourceId === formData.resourceIdValue);
      }

      if (resource) {
        setSelectedResource({
          id: resource.id,
          name: resource.name || '',
          resourceId: resource.resourceId || ''
        });
        // Update formData if resourceId wasn't set or doesn't match
        if (!formData.resourceId || formData.resourceId !== resource.id) {
          setFormData(prev => ({
            ...prev,
            resourceId: resource.id,
            resourceIdValue: resource.resourceId || prev.resourceIdValue
          }));
        }
      }
    }
  }, [formData.resourceId, formData.resourceIdValue, resourcesData, isOtherResource]);

  // Auto-select primary contact when customer/engagement changes
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  // Pre-fill form when problem data changes
  useEffect(() => {
    if (problem && isOpen) {
      // Convert to enum format (high_1, medium_2, low_3) if not already
      const toEnumFormat = (value: string): string => {
        if (!value) return 'low_3';
        // Already in enum format
        if (value === 'high_1' || value === 'medium_2' || value === 'low_3') return value;
        // Convert from simple format
        if (value === 'high' || value.startsWith('high')) return 'high_1';
        if (value === 'medium' || value.startsWith('medium')) return 'medium_2';
        return 'low_3';
      };

      const impact = toEnumFormat(problem.impact);
      const urgency = toEnumFormat(problem.urgency);
      const calculatedPriority = calculatePriority(impact, urgency);

      // Check if problem has otherResourceNames (custom resources)
      const hasOtherResources = problem.otherResourceNames && problem.otherResourceNames.trim();
      setIsOtherResource(!!hasOtherResources);

      setFormData({
        contactId: problem.contactId || problem.User_Case_contactIdToUser?.id || '',
        impact: impact,
        urgency: urgency,
        priority: calculatedPriority,
        problemStatement: problem.description || '',
        problemDate: new Date().toISOString().split('T')[0],
        customerId: problem.customerId || problem.Customer?.id || '',
        engagementId: problem.engagementId || problem.Engagement?.id || '',
        resourceId: hasOtherResources ? '' : (problem.resourceId || problem.Resource?.id || ''),
        resourceIdValue: hasOtherResources ? '' : (problem.resourceIdValue || problem.Resource?.resourceId || ''),
        otherResourceNames: problem.otherResourceNames || '',
        assignmentGroupId: problem.assignmentGroupId || problem.Group?.id || '',
        assignedTo: '' // Clear assigned user initially - will be validated after groups load
      });

      // Set selected resource if Resource is available (backend returns Resource, not EngagementResource)
      if (!hasOtherResources && problem.Resource) {
        setSelectedResource({
          id: problem.Resource.id,
          name: problem.Resource.name || '',
          resourceId: problem.Resource.resourceId || ''
        });
      } else {
        setSelectedResource(null);
      }
      setErrorMessage(null);
      setValidationErrors({});
    }
  }, [problem, isOpen]);

  // Clear assigned user if assignment group is not in available options
  useEffect(() => {
    if (isOpen && groupsData?.groups && formData.assignmentGroupId) {
      const groupExists = groupsData.groups.some((g: any) => g.id === formData.assignmentGroupId);
      if (!groupExists) {
        // Assignment group from source problem is not in available options - clear both
        setFormData(prev => ({
          ...prev,
          assignmentGroupId: '',
          assignedTo: ''
        }));
      }
    }
  }, [isOpen, groupsData, formData.assignmentGroupId]);

  const duplicateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/problems', data);
      return response.data;
    },
    onSuccess: async (data) => {
      try {
        await api.post(`/cases/${data.problem.id}/related-records`, {
          relationships: [{
            targetId: problem.id,
            relationshipType: 'RELATED_TO'
          }],
          source: 'duplicate'
        });
      } catch (error) {
        console.error('Failed to create related records:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['problems'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['related-records', problem.id] });
      
      // Call onSuccess callback with new problem number if provided
      if (onSuccess) {
        const newProblemNumber = data?.problem?.number || data?.number || data?.problem?.id;
        onSuccess(newProblemNumber);
      }
      
      onClose();
      navigate(`/problems/${data.problem.id}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to duplicate problem';
      setErrorMessage(errorMsg);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};

    if (!formData.contactId) {
      errors.contactId = 'Contact is required';
    }
    if (!formData.impact) {
      errors.impact = 'Impact is required';
    }
    if (!formData.urgency) {
      errors.urgency = 'Urgency is required';
    }
    if (!formData.problemStatement.trim()) {
      errors.problemStatement = 'Problem Statement is required';
    }
    if (!formData.problemDate) {
      errors.problemDate = 'Problem Date is required';
    }
    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    if (!formData.assignmentGroupId) {
      errors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setValidationErrors({});
    setErrorMessage(null);

    const payload: any = {
      shortDescription: problem.shortDescription ? `Copy of ${problem.shortDescription}` : 'Copy of Problem',
      description: formData.problemStatement,
      priority: formData.priority,
      impact: formData.impact,
      urgency: formData.urgency,
      contactId: formData.contactId,
      problemDate: formData.problemDate,
      assignedTo: formData.assignedTo,
      assignmentGroupId: formData.assignmentGroupId || undefined,
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      // Copy additional fields from original problem
      rcaPresentedById: problem.rcaPresentedById || undefined,
      reportedIssue: problem.reportedIssue || undefined,
      actualServiceImpact: problem.actualServiceImpact || undefined,
      briefAnalysis: problem.briefAnalysis || undefined,
      rootCause: problem.rootCause || undefined,
      detailedAnalysis: problem.detailedAnalysis || undefined,
      correctiveActions: problem.correctiveActions || undefined,
      chronologyOfEvents: problem.chronologyOfEvents || undefined,
      problemStatement: problem.problemStatement || undefined
    };

    // Handle resource: either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      payload.otherResourceNames = formData.otherResourceNames.trim();
    } else if (formData.resourceId) {
      payload.resourceId = formData.resourceId;
      payload.resourceIdValue = formData.resourceIdValue || undefined;
    }

    duplicateMutation.mutate(payload);
  };

  const customers = customersData?.customers || [];
  const allEngagements = engagementsData?.engagements || [];
  const engagements = formData.customerId
    ? allEngagements.filter((e: any) => e.Customer?.id === formData.customerId)
    : allEngagements;
  const groups = groupsData?.groups || [];
  const resources = Array.isArray(resourcesData) ? resourcesData : [];
  const groupUsers = usersData || [];
  const contacts = contactsData?.contacts || [];

  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      'critical_1': 'Critical',
      'high_2': 'High',
      'moderate_3': 'Moderate',
      'low_4': 'Low',
      'planning_5': 'Planning'
    };
    return labels[priority] || 'Moderate';
  };

  // Filter functions for comboboxes
  const filteredContacts = contactQuery === ''
    ? contacts
    : contacts.filter((c: any) => `${c.name} ${c.email}`.toLowerCase().includes(contactQuery.toLowerCase()));

  const filteredImpactOptions = impactQuery === ''
    ? impactOptions
    : impactOptions.filter((o) => o.label.toLowerCase().includes(impactQuery.toLowerCase()));

  const filteredUrgencyOptions = urgencyQuery === ''
    ? urgencyOptions
    : urgencyOptions.filter((o) => o.label.toLowerCase().includes(urgencyQuery.toLowerCase()));

  const filteredCustomers = customerQuery === ''
    ? customers
    : customers.filter((c: any) => c.accountName.toLowerCase().includes(customerQuery.toLowerCase()));

  const filteredEngagements = engagementQuery === ''
    ? engagements
    : engagements.filter((e: any) => `${e.Customer?.accountName || ''} ${e.name}`.toLowerCase().includes(engagementQuery.toLowerCase()));

  const filteredResources = resourceQuery === ''
    ? resources
    : resources.filter((r: any) => (r.name || r.resourceId || '').toLowerCase().includes(resourceQuery.toLowerCase()));

  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((g: any) => g.name.toLowerCase().includes(groupQuery.toLowerCase()));

  const filteredUsers = assignedToQuery === ''
    ? groupUsers
    : groupUsers.filter((u: any) => {
        const name = u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email;
        return name.toLowerCase().includes(assignedToQuery.toLowerCase());
      });

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Duplicate Problem {problem?.number}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
                  {errorMessage && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errorMessage}</div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Row 1: Customer and Engagement */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Customer */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Customer <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.customerId}
                            onChange={(value: string) => {
                              setFormData({ ...formData, customerId: value || '', engagementId: '', contactId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                              setSelectedResource(null);
                              setIsOtherResource(false);
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.customerId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(id: string) => {
                                  const customer = customers.find((c: any) => c.id === id);
                                  if (customer) return customer.accountName;
                                  // Fallback to problem's original customer data
                                  if (problem?.Customer && problem.Customer.id === id) {
                                    return problem.Customer.accountName || '';
                                  }
                                  return '';
                                }}
                                onChange={(e) => setCustomerQuery(e.target.value)}
                                placeholder="Select Customer"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setCustomerQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredCustomers.map((customer: any) => (
                                    <Combobox.Option
                                      key={customer.id}
                                      value={customer.id}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{customer.accountName}</span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.customerId && <p className="mt-1 text-sm text-red-600">{validationErrors.customerId}</p>}
                        </div>
                      </div>

                      {/* Engagement */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Engagement <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.engagementId}
                            onChange={(value: string) => {
                              setFormData({ ...formData, engagementId: value || '', contactId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                              setSelectedResource(null);
                              setIsOtherResource(false);
                            }}
                            disabled={!formData.customerId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.engagementId ? 'border-red-500' : 'border-gray-300'} ${!formData.customerId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(id: string) => {
                                  const eng = engagements.find((e: any) => e.id === id);
                                  if (eng) return `${eng.Customer?.accountName || ''} - ${eng.name}`;
                                  // Fallback to problem's original engagement data
                                  if (problem?.Engagement && problem.Engagement.id === id) {
                                    const customerName = problem.Customer?.accountName || problem.Engagement?.Customer?.accountName || '';
                                    return `${customerName} - ${problem.Engagement.name}`;
                                  }
                                  return '';
                                }}
                                onChange={(e) => setEngagementQuery(e.target.value)}
                                placeholder={formData.customerId ? "Select Engagement" : "Select Customer first"}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setEngagementQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredEngagements.map((engagement: any) => (
                                    <Combobox.Option
                                      key={engagement.id}
                                      value={engagement.id}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{engagement.Customer?.accountName || ''} - {engagement.name}</span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.engagementId && <p className="mt-1 text-sm text-red-600">{validationErrors.engagementId}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Contact */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Contact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Contact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.contactId}
                            onChange={(value: string) => setFormData({ ...formData, contactId: value || '' })}
                            disabled={!formData.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.contactId ? 'border-red-500' : 'border-gray-300'} ${!formData.engagementId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(id: string) => {
                                  const contact = contacts.find((c: any) => c.id === id);
                                  if (contact) return contact.name;
                                  // Fallback to problem's original contact data
                                  const problemContact = problem?.User_Case_contactIdToUser || problem?.Contact;
                                  if (problemContact && problemContact.id === id) {
                                    const name = `${problemContact.firstName || ''} ${problemContact.lastName || ''}`.trim();
                                    return name || problemContact.email || '';
                                  }
                                  return '';
                                }}
                                onChange={(e) => setContactQuery(e.target.value)}
                                placeholder={formData.customerId ? "Select Contact" : "Select Customer first"}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setContactQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredContacts.map((contact: any) => (
                                    <Combobox.Option
                                      key={contact.id}
                                      value={contact.id}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{contact.name}</span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.contactId && <p className="mt-1 text-sm text-red-600">{validationErrors.contactId}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Impact and Urgency */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Impact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Impact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.impact}
                            onChange={(value: string) => setFormData({ ...formData, impact: value || 'low_3' })}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.impact ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(val: string) => impactOptions.find((o) => o.value === val)?.label || ''}
                                onChange={(e) => setImpactQuery(e.target.value)}
                                placeholder="Select Impact"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setImpactQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredImpactOptions.map((option) => (
                                    <Combobox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{option.label}</span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.impact && <p className="mt-1 text-sm text-red-600">{validationErrors.impact}</p>}
                        </div>
                      </div>

                      {/* Urgency */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Urgency <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.urgency}
                            onChange={(value: string) => setFormData({ ...formData, urgency: value || 'low_3' })}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.urgency ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(val: string) => urgencyOptions.find((o) => o.value === val)?.label || ''}
                                onChange={(e) => setUrgencyQuery(e.target.value)}
                                placeholder="Select Urgency"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setUrgencyQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredUrgencyOptions.map((option) => (
                                    <Combobox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{option.label}</span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.urgency && <p className="mt-1 text-sm text-red-600">{validationErrors.urgency}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Priority and Problem Date */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Priority (Auto-calculated) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Priority
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={formData.priority ? getPriorityLabel(formData.priority) : ''}
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            placeholder="Auto-calculated"
                          />
                        </div>
                      </div>

                      {/* Problem Date */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Problem Date <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <DateTimePickerInput
                            value={formData.problemDate}
                            max={getToday()}
                            onChange={(value) => setFormData({ ...formData, problemDate: value })}
                            showTimeSelect={false}
                            error={!!validationErrors.problemDate}
                          />
                          {validationErrors.problemDate && <p className="mt-1 text-sm text-red-600">{validationErrors.problemDate}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Problem Statement */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Problem Statement <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.problemStatement}
                          onChange={(value) => setFormData({ ...formData, problemStatement: value })}
                          placeholder="Enter problem statement..."
                          className={validationErrors.problemStatement ? 'border-red-500' : ''}
                        />
                        {validationErrors.problemStatement && <p className="mt-1 text-sm text-red-600">{validationErrors.problemStatement}</p>}
                      </div>
                    </div>

                    {/* Row 6: Resource and Resource ID */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resource */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Resource
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={isOtherResource ? '__OTHER__' : formData.resourceId}
                            onChange={(value: string) => {
                              if (value === '__OTHER__') {
                                setIsOtherResource(true);
                                setSelectedResource(null);
                                setFormData({ ...formData, resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                              } else {
                                setIsOtherResource(false);
                                const resource = resources.find((r: any) => r.id === value);
                                if (resource) {
                                  setSelectedResource({
                                    id: resource.id,
                                    name: resource.name || '',
                                    resourceId: resource.resourceId || ''
                                  });
                                  setFormData({ ...formData, resourceId: value || '', resourceIdValue: resource.resourceId || '', otherResourceNames: '' });
                                } else {
                                  setFormData({ ...formData, resourceId: value || '', otherResourceNames: '' });
                                }
                              }
                            }}
                            disabled={!formData.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border border-gray-300 ${!formData.engagementId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(id: string) => {
                                  if (isOtherResource || id === '__OTHER__') return 'Others (specify custom resources)';
                                  if (id) {
                                    // First try to find in loaded resources
                                    const resource = resources.find((r: any) => r.id === id);
                                    if (resource) return resource.name || resource.resourceId;
                                    // Fallback to selectedResource state
                                    if (selectedResource && selectedResource.id === id) {
                                      return selectedResource.name;
                                    }
                                    // Fallback to problem's original resource data
                                    if (problem?.Resource && problem.Resource.id === id) {
                                      return problem.Resource.name || '';
                                    }
                                  }
                                  return '';
                                }}
                                onChange={(e) => setResourceQuery(e.target.value)}
                                placeholder={formData.engagementId ? "Search resource..." : "Select Engagement first"}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setResourceQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredResources.map((resource: any) => (
                                    <Combobox.Option
                                      key={resource.id}
                                      value={resource.id}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected && !isOtherResource ? 'font-semibold' : 'font-normal'}`}>{resource.name || resource.resourceId}</span>
                                          {selected && !isOtherResource && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                  {/* Others option */}
                                  <Combobox.Option
                                    key="__OTHER__"
                                    value="__OTHER__"
                                    className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                  >
                                    {({ active }) => (
                                      <>
                                        <span className={`block truncate ${isOtherResource ? 'font-semibold' : 'font-normal'}`}>Others (specify custom resources)</span>
                                        {isOtherResource && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        </div>
                      </div>

                      {/* Resource ID (readonly) or Other Resource Names input */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          {isOtherResource ? 'Resource Names' : 'Resource ID'}
                        </label>
                        <div className="flex-1">
                          {isOtherResource ? (
                            <div>
                              <input
                                type="text"
                                value={formData.otherResourceNames}
                                onChange={(e) => setFormData({ ...formData, otherResourceNames: e.target.value })}
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 sm:text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Enter resource names (comma separated)"
                              />
                              <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={formData.resourceIdValue}
                              disabled
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                              placeholder="Auto-filled from Resource"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 7: Assignment Group and Assigned To */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Assignment Group */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.assignmentGroupId}
                            onChange={(value: string) => setFormData({ ...formData, assignmentGroupId: value || '', assignedTo: '' })}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.assignmentGroupId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(id: string) => {
                                  const group = groups.find((g: any) => g.id === id);
                                  if (group) return group.name;
                                  // Fallback to problem's original assignment group data
                                  if (problem?.Group && problem.Group.id === id) {
                                    return problem.Group.name || '';
                                  }
                                  return '';
                                }}
                                onChange={(e) => setGroupQuery(e.target.value)}
                                placeholder="Select Assignment Group"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setGroupQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredGroups.map((group: any) => (
                                    <Combobox.Option
                                      key={group.id}
                                      value={group.id}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{group.name}</span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.assignmentGroupId && <p className="mt-1 text-sm text-red-600">{validationErrors.assignmentGroupId}</p>}
                        </div>
                      </div>

                      {/* Assigned To */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assigned To
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.assignedTo}
                            onChange={(value: string) => setFormData({ ...formData, assignedTo: value || '' })}
                            disabled={!formData.assignmentGroupId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full rounded-md border ${validationErrors.assignedTo ? 'border-red-500' : 'border-gray-300'} ${!formData.assignmentGroupId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} shadow-sm py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500`}
                                displayValue={(id: string) => {
                                  const user = groupUsers.find((u: any) => u.id === id);
                                  if (user) return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;
                                  // Fallback to problem's original assigned to data
                                  const assignedTo = problem?.User_Case_assignedToIdToUser || problem?.AssignedTo;
                                  if (assignedTo && assignedTo.id === id) {
                                    return assignedTo.firstName && assignedTo.lastName ? `${assignedTo.firstName} ${assignedTo.lastName}` : assignedTo.email || '';
                                  }
                                  return '';
                                }}
                                onChange={(e) => setAssignedToQuery(e.target.value)}
                                placeholder={formData.assignmentGroupId ? "Select Assigned To" : "Select Assignment Group first"}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setAssignedToQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredUsers.map((user: any) => (
                                    <Combobox.Option
                                      key={user.id}
                                      value={user.id}
                                      className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.assignedTo && <p className="mt-1 text-sm text-red-600">{validationErrors.assignedTo}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={duplicateMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                    >
                      {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Problem'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DuplicateProblemModal;
